import { CourseCard } from '@inflearn/course-card';
import { useLang } from '@inflearn/i18n-tools';
import Link from 'next/link';
import type { SyntheticEvent } from 'react';

import type {
  ClientApiV1CoursesCourseIdLikeUpdateVariables,
  UserProfileResponse,
} from '../../../../../resources/@types/uccSchema';
import { COURSE_URL } from '../../../../constants/urls';
import { useCart } from '../../../../hooks/useCartProfile'; // TODO 추후 useCart 파일 하나로 통합 예정
import useCheckLoginSession from '../../../../hooks/useCheckLoginSession';
import {
  type UserProfileCourseDto,
  adaptToCourseCardListPriceProp,
  adaptToCourseCardPromotionProp,
} from '../../../../utils/course/courseAdaptor';

type UseReactionMutateProps = {
  courseId: number;
  profileId: number;
};

export type UseReactionMutate = ({ courseId, profileId }: UseReactionMutateProps) => {
  reactionMutate: ({ status }: ClientApiV1CoursesCourseIdLikeUpdateVariables) => void;
};

type Props = {
  profile: UserProfileResponse;
  course: UserProfileCourseDto;
  useReactionMutate: UseReactionMutate;
  isLoading?: boolean;
  onOpenLoginModal: () => void;
};

export default function CourseItem({
  course,
  profile,
  useReactionMutate,
  isLoading = false,
  onOpenLoginModal,
}: Props) {
  const { lang } = useLang();
  const { isLoggedIn, isAdmin } = useCheckLoginSession();
  const isInstructor = Boolean(profile.isInstructor);

  const { reactionMutate } = useReactionMutate({
    profileId: profile.id,
    courseId: course.id,
  });

  const { isInCart, addToCart, removeFromCart } = useCart({
    initialValue: course.cart.isInCart,
    course,
  });

  const onClickCard = (e: SyntheticEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    /**
     * @NOTE 강의 좋아요 버튼 등. 유저 액션 버튼 클릭 시 링크 이동 막기
     */
    if (e.target instanceof SVGElement && e.target.closest('button')) {
      return;
    }

    if (e.target instanceof HTMLElement && e.target.nodeName === 'BUTTON') {
      return;
    }

    window.location.href = e.currentTarget.href;
  };

  const handleClickLikeButton = () => {
    if (!isLoggedIn) {
      onOpenLoginModal();

      return;
    }

    reactionMutate({ status: course.hasMyReaction ? 'UNLIKE' : 'LIKE' });
  };

  const handleClickCartButton = () => {
    if (!isLoggedIn) {
      onOpenLoginModal();

      return;
    }

    if (isInCart) {
      removeFromCart({ courseIds: [course.id] });

      return;
    }

    addToCart({ courseIds: [course.id] });
  };

  return (
    <>
      <Link href={COURSE_URL.COURSE_DETAIL(course.course.slug)} onClick={onClickCard}>
        <CourseCard
          {...course}
          course={course.course}
          cart={{
            ...course.cart,
            isInCart,
          }}
          promotion={adaptToCourseCardPromotionProp(course)}
          listPrice={adaptToCourseCardListPriceProp(course)}
          isAdmin={isAdmin}
          isInstructor={isInstructor}
          isHideInstructorName
          isLoading={isLoading}
          hasMentoring={course.hasMentoring}
          saleBadgeType={'TEXT' as const}
          tagTitleType="FLOATING"
          type={course.type}
          onClickLikeButton={handleClickLikeButton}
          onClickCartButton={handleClickCartButton}
          lang={lang}
          enableABTest={false}
        />
      </Link>
    </>
  );
}
